import { useEffect, useReducer, useMemo } from 'react';
import axios from 'axios';
import { TokenAsset } from '@types';
import { notify } from '@lib/bugsnag';
import { NotifiableError } from '@bugsnag/core';

interface UseTokenAssetsReducerState {
  data: TokenAsset[] | null;
  hasError: boolean;
  isLoading: boolean;
}

const initState: UseTokenAssetsReducerState = {
  data: null,
  hasError: false,
  isLoading: false,
};

const GET_PENDING = 'GET_PENDING';

const GET_FULFILLED = 'GET_FULFILLED';

const GET_REJECTED = 'GET_REJECTED';

interface GetPendingAction {
  type: typeof GET_PENDING;
}

interface GetFulfilledAction {
  data: TokenAsset[];
  type: typeof GET_FULFILLED;
}

interface GetRejectedAction {
  type: typeof GET_REJECTED;
}

type UseTokenAssetsReducerAction =
  | GetPendingAction
  | GetFulfilledAction
  | GetRejectedAction;

function reducer(
  state: UseTokenAssetsReducerState,
  action: UseTokenAssetsReducerAction
): UseTokenAssetsReducerState {
  switch (action.type) {
    case GET_PENDING:
      return { ...state, isLoading: true, hasError: false };
    case GET_FULFILLED:
      return { ...state, isLoading: false, data: action.data };
    case GET_REJECTED:
      return { ...state, isLoading: false, hasError: true };
    default:
      return { ...state };
  }
}

export type UseTokenAssetsResult = UseTokenAssetsReducerState;

const ENDPOINT = process.env.NEXT_PUBLIC_TOKEN_ASSET_URL || '';

function useTokenAssets(): UseTokenAssetsResult {
  const [state, dispatch] = useReducer(reducer, initState);

  useEffect(() => {
    async function getTokens(): Promise<void> {
      dispatch({ type: GET_PENDING });

      try {
        const { data } = await axios.get<TokenAsset[]>(ENDPOINT);
        dispatch({ data, type: GET_FULFILLED });
      } catch (error) {
        notify(error as NotifiableError);
        dispatch({ type: GET_REJECTED });
      }
    }

    getTokens();
  }, []);

  return useMemo(
    () => ({
      data: state.data,
      hasError: state.hasError,
      isLoading: state.isLoading,
    }),
    [state]
  );
}

export default useTokenAssets;
